import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import React from "react";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/components/ui/use-toast";

export function DueDateSelect({ task, showLabel = false, onTaskChange, organizationSlug, newTask = false }) {
  const [open, setOpen] = React.useState(false);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const { axiosInstance } = useAuth();
  const { toast } = useToast();

  const handleDateSelect = async (date) => {
    const originalTask = { ...task };
    try {
      setIsUpdating(true);

      // Optimistically update the UI
      onTaskChange({
        ...task,
        dueDate: date
      });

      setOpen(false);

      if (task.key) {
      // Make API request to update the task
      const response = await axiosInstance.patch(
        `/tasks/${organizationSlug}/${task.key}`,
        {
          dueDate: date
        }
      );

      // Update with server response
        onTaskChange(response.data);
      }
    } catch (error) {
      console.error("Error updating due date:", error);
      // Revert to original state on error
      onTaskChange(originalTask);
      toast({
        variant: "destructive",
        description: "Failed to update due date",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemoveDueDate = async () => {
    const originalTask = { ...task };
    try {
      setIsUpdating(true);

      // Optimistically update the UI
      onTaskChange({
        ...task,
        dueDate: null
      });

      setOpen(false);

      if (task.key) {
        // Make API request to update the task
        const response = await axiosInstance.patch(
          `/tasks/${organizationSlug}/${task.key}`,
          {
            dueDate: null
          }
        );

        // Update with server response
        onTaskChange(response.data);
      }
    } catch (error) {
      console.error("Error removing due date:", error);
      // Revert to original state on error
      onTaskChange(originalTask);
      toast({
        variant: "destructive",
        description: "Failed to remove due date",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="space-y-2">
      {showLabel && (
        <div className="text-sm font-normal text-neutral-500 dark:text-neutral-600">Due Date</div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "text-xs h-7 justify-start text-left font-normal",
              !task.dueDate && "text-primary",
              isUpdating && "opacity-50 cursor-not-allowed"
            )}
            disabled={isUpdating}
          >
            {task.dueDate ? (
              format(new Date(task.dueDate), "MMM d, yyyy")
            ) : (
              "Set due date"
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start" side={`${newTask && "top"}`}>
          <Calendar
            mode="single"
            selected={task.dueDate}
            onSelect={handleDateSelect}
            initialFocus
            disabled={isUpdating}
          />
          {task.dueDate && (
            <div className="p-2 border-t">
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={handleRemoveDueDate}
                disabled={isUpdating}
              >
                Remove due date
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}